<template>
  <div
    class="play-input"
    :class="[`play-input--${size}`, { 'is-error': !!error, 'is-disabled': disabled }]"
  >
    <label
      v-if="label"
      :for="fieldId"
      class="play-input__label"
    >
      {{ label }}
      <span
        v-if="required"
        class="play-input__req"
        aria-hidden="true"
      >*</span>
    </label>

    <textarea
      v-if="textarea"
      :id="fieldId"
      class="play-input__field play-input__field--textarea"
      :value="value"
      :placeholder="placeholder"
      :disabled="disabled"
      :rows="rows"
      :aria-invalid="!!error"
      v-bind="$attrs"
      v-on="fieldListeners"
    />
    <div
      v-else
      class="play-input__control"
      :class="{ 'has-prefix': !!$slots.prefix, 'has-suffix': !!$slots.suffix }"
    >
      <!-- Leading adornment: fixed prefix (e.g. a URL host) -->
      <span
        v-if="$slots.prefix"
        class="play-input__prefix"
      >
        <slot name="prefix" />
      </span>
      <input
        :id="fieldId"
        class="play-input__field"
        :type="type"
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        :aria-invalid="!!error"
        v-bind="$attrs"
        v-on="fieldListeners"
      >
      <!-- Trailing adornment: icon button (password toggle), unit, etc. -->
      <span
        v-if="$slots.suffix"
        class="play-input__suffix"
      >
        <slot name="suffix" />
      </span>
    </div>

    <p
      v-if="error || hint"
      class="play-input__msg"
      :class="error ? 'play-input__msg--error' : 'play-input__msg--hint'"
    >
      {{ error || hint }}
    </p>
  </div>
</template>

<script>
/**
 * PlayInput — text field primitive for the Play system component layer.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Replaces the
 * bootstrap-vue <b-form-group> + <b-form-input>/<b-form-textarea> combo in the
 * rebrand — label, field, validation message and the error color (--c-danger)
 * all live in one component.
 *
 * Renders an <input> (any native `type`) or a <textarea> via `textarea`.
 * Drive it with v-model; pass `error` for the invalid state + message, `hint`
 * for neutral helper text.
 *
 * VUE3-MIGRATION: two Vue 2-isms, both isolated:
 *   1. `model: { prop: 'value', event: 'input' }` — Vue 3 uses
 *      `modelValue` / `update:modelValue`. Change this block + the emit name.
 *   2. `fieldListeners` spreads `$listeners` (removed in Vue 3, merged into
 *      $attrs). On migration drop the spread; keep the local input handler.
 */
let uid = 0

export default {
  name: 'PlayInput',
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    /** Native input type (ignored when `textarea` is set) */
    type: {
      type: String,
      default: 'text',
    },
    textarea: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: [String, Number],
      default: 3,
    },
    placeholder: {
      type: String,
      default: '',
    },
    /** Error message — its presence drives the invalid styling */
    error: {
      type: String,
      default: '',
    },
    /** Neutral helper text shown when there is no error */
    hint: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg'].includes(v),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /** Renders a `*` next to the label (visual only) */
    required: {
      type: Boolean,
      default: false,
    },
    /** Optional explicit id; auto-generated for label association otherwise */
    id: {
      type: String,
      default: '',
    },
  },
  data() {
    uid += 1
    return {
      generatedId: `play-input-${uid}`,
    }
  },
  computed: {
    fieldId() {
      return this.id || this.generatedId
    },
    /** Forward consumer listeners (@blur/@focus/…) while owning `input`. */
    fieldListeners() {
      return {
        ...this.$listeners,
        input: e => this.$emit('input', e.target.value),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.play-input {
  display: block;

  &__label {
    display: block;
    margin-bottom: 8px;
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 14px;
    color: var(--text-strong);
  }

  &__req { color: var(--c-danger); }

  /* Shared field typography — the bordered box lives on the control (inputs)
     or on the textarea element itself. */
  &__field {
    display: block;
    width: 100%;
    font-family: var(--font-body);
    font-weight: 500;
    color: var(--text-strong);
    -webkit-tap-highlight-color: transparent;

    &::placeholder { color: var(--text-muted); }

    /* Browser autofill paints the (transparent) input itself, giving a blue
       box with square corners inside the rounded control. Keep the field
       transparent so the control's soft-fill + radius show through; only
       carry over the text/caret colour. */
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-text-fill-color: var(--text-strong);
      caret-color: var(--text-strong);
      border-radius: var(--radius);
      transition: background-color 100000s ease-in-out 0s;
    }
  }

  /* ── Input: the control is the bordered box; the input sits transparent
     inside it, next to optional prefix / suffix affixes. ── */
  &__control {
    display: flex;
    align-items: stretch;
    background: var(--surface-sunk);
    border: var(--border-w) solid transparent;
    border-radius: var(--radius);
    box-shadow: none;
    transition:
      background var(--dur-fast) var(--ease-out),
      box-shadow var(--dur-fast) var(--ease-out),
      border-color var(--dur-fast) var(--ease-out);

    &:focus-within {
      background: var(--surface-card);
      border-color: var(--c-secondary);
      box-shadow: 0 0 0 3px var(--c-secondary-soft);
    }

    .play-input__field {
      flex: 1;
      min-width: 0;
      border: none;
      background: transparent;
      box-shadow: none;
      outline: none;
    }

    &.has-prefix .play-input__field { padding-inline-start: 6px; }
    &.has-suffix .play-input__field { padding-inline-end: 6px; }
  }

  &__prefix,
  &__suffix {
    display: inline-flex;
    align-items: center;
    flex: none;
    color: var(--text-muted);
  }
  &__prefix {
    padding-inline-start: 14px;
    font-family: var(--font-display);
    font-weight: 700;
    white-space: nowrap;
  }
  &__suffix { padding-inline-end: 14px; }

  /* ── Textarea keeps its own bordered box ── */
  &__field--textarea {
    background: var(--surface-sunk);
    border: var(--border-w) solid transparent;
    border-radius: var(--radius);
    box-shadow: none;
    transition:
      background var(--dur-fast) var(--ease-out),
      box-shadow var(--dur-fast) var(--ease-out),
      border-color var(--dur-fast) var(--ease-out);
    resize: vertical;
    min-height: 84px;
    line-height: 1.45;

    &:focus {
      outline: none;
      background: var(--surface-card);
      border-color: var(--c-secondary);
      box-shadow: 0 0 0 3px var(--c-secondary-soft);
    }
  }

  &__msg {
    margin: 6px 0 0;
    font-family: var(--font-body);
    font-size: 12.5px;
    font-weight: 500;

    &--error { color: var(--c-danger-strong); }
    &--hint { color: var(--text-muted); }
  }

  /* ── Sizes (padding + font on the field; radius on the box) ── */
  &--sm {
    .play-input__field { padding: 9px 13px; font-size: 14px; }
    .play-input__control,
    .play-input__field--textarea { border-radius: var(--radius-sm); }
  }
  &--md .play-input__field { padding: 12px 15px; font-size: 15px; }
  &--lg {
    .play-input__field { padding: 15px 18px; font-size: 16px; }
    .play-input__control,
    .play-input__field--textarea { border-radius: var(--radius-lg); }
  }

  /* ── Invalid ── */
  &.is-error {
    .play-input__control,
    .play-input__field--textarea {
      background: var(--c-danger-soft);
      border-color: transparent;
      box-shadow: none;
    }
    .play-input__control:focus-within,
    .play-input__field--textarea:focus {
      background: var(--surface-card);
      border-color: var(--c-danger);
      box-shadow: 0 0 0 3px var(--c-danger-soft);
    }
  }

  /* ── Disabled ── */
  &.is-disabled {
    opacity: .55;

    .play-input__control,
    .play-input__field { pointer-events: none; }
  }
}
</style>
